import { useCallback, useEffect } from 'react';

import { useMutation } from '@apollo/client';
import isEqual from 'lodash/isEqual';

import { UPDATE_USER_FCM_TOKENS } from '../../app/graphql/mutations';
import { useAppSelector } from '../../app/store/hooks';
import RequestFirebaseMessagingToken from '../../utils/firbase/RequestFirebaseMessagingToken';
import UnRegisterPushNotification from '../../utils/firbase/UnRegisterPushNotification';

/**
 * Headless helper component to manage Firebase Cloud Messaging (FCM) tokens.
 *
 * Responsibilities:
 * - Exposes window.onFCMToken so a native shell can push tokens into the web app.
 * - Requests/ensures an FCM token when running as a PWA (standalone display mode).
 * - Unregisters web push when not in PWA mode.
 * - Reads the latest token from localStorage and syncs it to the backend, deduplicated.
 * - Listens for 'push-token' CustomEvents to capture tokens sent asynchronously by native layers.
 *
 * This component renders nothing (returns null).
 */
export default function FirebaseToken() {
  const user = useAppSelector((state) => state.main.user);
  const userId = user?.id;
  const existingFcmTokens = user?.fcmTokens;
  const [updateUserFcmTokens] = useMutation(UPDATE_USER_FCM_TOKENS);

  // Bridge: allow native layers to provide tokens -> dispatch an app-level event we can listen to.
  if (typeof window !== 'undefined' && !window.onFCMToken) {
    window.onFCMToken = function (token: string) {
      window.dispatchEvent(new CustomEvent('push-token', { detail: token }));
    };
  }

  // Detect if the app is running as a standalone PWA (controls whether we register web push).
  const isStandalonePwa = window.matchMedia(
    '(display-mode: standalone)'
  ).matches;
  // Ensure we have a valid token in supported contexts; unregister when not applicable.
  const ensureFcmToken = useCallback(() => {
    if (userId) {
      if (isStandalonePwa) {
        RequestFirebaseMessagingToken({
          fcmTokens: existingFcmTokens,
          id: userId,
          updateUserFcmTokens,
        });
      } else {
        UnRegisterPushNotification();
      }
    }
  }, [userId, isStandalonePwa, existingFcmTokens, updateUserFcmTokens]);

  useEffect(() => {
    // Align web push registration with the current environment/user.
    ensureFcmToken();
    // Read any token placed in localStorage (by the web flow or native bridge) and sync to backend.
    const syncStoredTokenToBackend = () => {
      // Single source of truth: we store the latest token under 'pendingPushToken'.
      const storedNativePushToken =
        window.localStorage.getItem('pendingPushToken');
      // Require a valid user to perform backend updates.
      if (!userId) {
        console.error(
          'User ID is missing. Cannot update FCM tokens without a valid user ID.'
        );
        return;
      }

      // Abort if no token available yet.
      if (!storedNativePushToken) {
        console.warn(
          'FCM token is missing. Cannot update FCM tokens without a valid token.'
        );
        return;
      }

      // Add token to the user's list if missing and push to backend.
      if (Array.isArray(existingFcmTokens)) {
        const baseToken = storedNativePushToken;
        const updatedTokens = existingFcmTokens.includes(baseToken)
          ? existingFcmTokens
          : [...existingFcmTokens, baseToken];

        if (!isEqual(updatedTokens, existingFcmTokens)) {
          try {
            updateUserFcmTokens({
              variables: {
                id: userId,
                fcm_tokens: updatedTokens,
              },
            });
          } catch (error) {
            if (error instanceof Error) {
              console.error('Error updating FCM tokens:', error.message);
            } else {
              console.error(
                'An unknown error occurred during FCM token update:',
                error
              );
            }
          }
        } else {
          console.log('Token already exists in fcmTokens, no update needed.');
        }
      }
    };
    syncStoredTokenToBackend();
    // Listen for tokens provided asynchronously via the native bridge.
    const handlePushToken = (event: Event) => {
      const tokenFromApp = (event as MessageEvent & { detail: string }).detail;
      window.localStorage.setItem('pendingPushToken', tokenFromApp);
    };

    window.addEventListener('push-token', handlePushToken);

    return () => {
      // Cleanup on unmount.
      window.removeEventListener('push-token', handlePushToken);
    };
  }, [userId, ensureFcmToken, existingFcmTokens, updateUserFcmTokens]);

  return null;
}
